import { useState, useEffect } from "react";
import { useNavigate, useParams } from 'react-router-dom';
import "./ContactDetail.css";
import { ArrowBackIos } from '@mui/icons-material';
import {
  IconButton, CircularProgress, Typography, Button, useTheme, Box, Chip,
  LinearProgress, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Paper, Alert, Select, MenuItem, FormControl, InputLabel,
  Tabs, Tab,
} from '@mui/material';
import { tokens } from "../../theme";
import { api } from '../../services/auth';
import { useBrand } from '../../contexts/BrandContext';
import { loyaltyFetch, LOYALTY_API_BASE } from '../fidelite/hooks/loyaltyFetch';
import { useCustomerEntitlementsCatalog } from '../fidelite/hooks/useCustomerEntitlementsCatalog';
import CustomerCouponsEntitlementsTable from '../fidelite/components/shared/CustomerCouponsEntitlementsTable';
import EntitlementsHistoryPanel from '../fidelite/components/shared/EntitlementsHistoryPanel';
import SaleTransactionsPanel from '../fidelite/components/shared/SaleTransactionsPanel';
import CustomerValidityDatesPanel from '../fidelite/components/shared/CustomerValidityDatesPanel';
import { getPointsBalance, getTierLabel } from '../fidelite/utils/loyaltyDisplay';
import { TransactionStatusChip } from '../fidelite/utils/transactionUi';
import { usePostPurchasePolling } from '../fidelite/hooks/usePostPurchasePolling';
import {
  buildTransactionsByUserUrl,
  fetchCustomerLoyaltyBundle,
  loyaltyBrandHeaders,
  normalizeLoyaltyBrand,
  resolveContactEmailForLoyalty,
} from '../fidelite/utils/loyaltyCustomerApi';
import {
  LOYALTY_NOT_ENROLLED,
  getLoyaltyNotEnrolledMessage,
  isLoyaltyNotFoundHttpStatus,
} from '../fidelite/utils/loyaltyContactStatus';

export default function ContactDetail() {
  const [activeTab, setActiveTab] = useState("profilUnomi");
  const [contact, setContact] = useState(null);
  const [loading, setLoading] = useState(true);
  const loyaltyModuleActive = true;
  const navigate = useNavigate();
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  // ── Brand context (needed for loyalty API) ──────────────────
  const { currentBrand } = useBrand();
  const brandName = currentBrand?.name || "";
  const { statusLabels: catalogStatusLabels } = useCustomerEntitlementsCatalog(brandName);

  // ── Loyalty state ────────────────────────────────────────────
  const [loyaltyWallet, setLoyaltyWallet] = useState(null);
  const [loyaltyCustomer, setLoyaltyCustomer] = useState(null);
  const [loyaltyInfo, setLoyaltyInfo] = useState(null);
  const [loyaltyMovements, setLoyaltyMovements] = useState([]);
  const [loyaltyCouponsWithRewards, setLoyaltyCouponsWithRewards] = useState([]);
  const [rewardDefs, setRewardDefs] = useState({});
  const [loyaltyLoading, setLoyaltyLoading] = useState(false);
  const [loyaltyError, setLoyaltyError] = useState(null);
  const [loyaltyLoadErrors, setLoyaltyLoadErrors] = useState({});
  const [loyaltyFetched, setLoyaltyFetched] = useState(false);
  const [entitlementsTab, setEntitlementsTab] = useState(0);
  const [loyaltyRefreshToken, setLoyaltyRefreshToken] = useState(0);

  // ── Transactions state ───────────────────────────────────────
  const [transactions, setTransactions] = useState([]);
  const [transactionTypes, setTransactionTypes] = useState([]);
  const [selectedTxType, setSelectedTxType] = useState("all");
  const [txLoading, setTxLoading] = useState(false);
  const [txError, setTxError] = useState(null);
  const [txNotEnrolled, setTxNotEnrolled] = useState(false);
  const [txFetched, setTxFetched] = useState(false);

  const tabs = [
    { id: "profilUnomi", label: "Profil Unomi" },
    { id: "engagementMautic", label: "Engagement Email" },
    { id: "activiteUnomi", label: "Activité & Événements" },
  ];

  if (loyaltyModuleActive) {
    tabs.push({ id: "fideliteDetails", label: "Fidélité Détaillée" });
  }

  // ── Fetch Unomi contact ──────────────────────────────────────
  const fetchContactDetails = async (contactId) => {
    setLoading(true);
    try {
      const response = await api.get(`/profiles/${contactId}`);
      setContact(response.data.profile);
    } catch (error) {
      console.error('Erreur lors du chargement des détails:', error);
    } finally {
      setLoading(false);
    }
  };

  const { id } = useParams();
  const contactEmail = contact ? resolveContactEmailForLoyalty(contact, brandName) : "";
  const unomiProfileId = contact?.itemId || id || "";
  const loyaltyProfileId = loyaltyCustomer?.profile_id || unomiProfileId;
  const profileIdMismatch = Boolean(
    loyaltyCustomer?.profile_id
    && unomiProfileId
    && String(loyaltyCustomer.profile_id) !== String(unomiProfileId),
  );

  useEffect(() => {
    if (id) {
      fetchContactDetails(id);
    }
  }, [id]);

  // Reset loyalty / transactions when contact, email or brand changes.
  useEffect(() => {
    setLoyaltyWallet(null);
    setLoyaltyCustomer(null);
    setLoyaltyInfo(null);
    setLoyaltyMovements([]);
    setLoyaltyCouponsWithRewards([]);
    setRewardDefs({});
    setLoyaltyError(null);
    setLoyaltyLoadErrors({});
    setLoyaltyFetched(false);
    setTransactions([]);
    setTxError(null);
    setTxNotEnrolled(false);
    setTxFetched(false);
  }, [unomiProfileId, contactEmail, brandName]);

  // ── Fetch loyalty data (lazy — only when tab becomes active) ──
  const fetchLoyaltyData = async (entryProfileId, email = "") => {
    if (!entryProfileId || !brandName) return;
    setLoyaltyLoading(true);
    setLoyaltyError(null);
    setLoyaltyLoadErrors({});
    const emailParam = String(email || contactEmail || "").trim();

    try {
      const bundle = await fetchCustomerLoyaltyBundle(brandName, entryProfileId, emailParam);
      if (!bundle.ok) {
        if (isLoyaltyNotFoundHttpStatus(bundle.status)) {
          setLoyaltyError(LOYALTY_NOT_ENROLLED);
          return;
        }
        throw new Error(bundle.fatalError || "Erreur de récupération du profil client fidélité.");
      }

      setLoyaltyLoadErrors(bundle.errors || {});
      setLoyaltyInfo(bundle.loyalty);
      setLoyaltyMovements(bundle.movements);
      setLoyaltyCouponsWithRewards(bundle.couponsWithRewards);

      const customerData = bundle.customer || {
        profile_id: bundle.activeProfileId,
        points_balance: bundle.loyalty?.pointsBalance ?? 0,
        points_expires_at: bundle.loyalty?.pointsExpiresAt ?? null,
        loyalty_status: bundle.loyalty?.loyaltyStatus ?? null,
        loyalty_status_expires_at: bundle.loyalty?.statusExpiresAt ?? null,
      };
      setLoyaltyCustomer(customerData);
      setLoyaltyWallet({
        pointsBalance: bundle.loyalty?.pointsBalance ?? customerData?.points_balance ?? 0,
        loyaltyStatus: bundle.loyalty?.loyaltyStatus ?? customerData?.loyalty_status ?? null,
        lifetimePoints: customerData?.lifetime_points ?? 0,
      });

      const items = bundle.couponsWithRewards;
      const rewardIds = new Set();
      items.forEach((it) => {
        const rewards = Array.isArray(it?.rewards) ? it.rewards : [];
        rewards.forEach((r) => { if (r?.reward_id) rewardIds.add(String(r.reward_id)); });
      });
      if (rewardIds.size > 0) {
        const brand = normalizeLoyaltyBrand(brandName);
        const headers = loyaltyBrandHeaders(brand);
        const rRes = await loyaltyFetch(
          `${LOYALTY_API_BASE}/rewards?brand=${encodeURIComponent(brand)}`,
          { headers },
        );
        if (rRes.ok) {
          const rData = await rRes.json();
          const defMap = {};
          rData.forEach(r => defMap[r.id] = r);
          setRewardDefs(defMap);
        }
      }
    } catch (err) {
      setLoyaltyError(err.message || "Erreur inconnue.");
    } finally {
      setLoyaltyLoading(false);
      setLoyaltyFetched(true);
    }
  };

  // ── Fetch Transactions (lazy — only when activiteUnomi tab becomes active) ──
  const fetchTransactions = async (entryProfileId, type, email = "") => {
    if (!entryProfileId || !brandName) return;
    setTxLoading(true);
    setTxError(null);
    setTxNotEnrolled(false);
    const headers = loyaltyBrandHeaders(brandName);
    const emailParam = String(email || contactEmail || "").trim();

    if (!emailParam) {
      setTransactions([]);
      setTxNotEnrolled(true);
      setTxLoading(false);
      setTxFetched(true);
      return;
    }

    const activeProfileId = loyaltyCustomer?.profile_id || entryProfileId;

    try {
      if (transactionTypes.length === 0) {
        const brand = normalizeLoyaltyBrand(brandName);
        const typesRes = await loyaltyFetch(`${LOYALTY_API_BASE}/admin/transaction-types?brand=${encodeURIComponent(brand)}`, { headers });
        if (typesRes.ok) {
          const typesData = await typesRes.json();
          setTransactionTypes(typesData);
        }
      }

      const endpoint = buildTransactionsByUserUrl({
        brandName,
        profileId: activeProfileId,
        email: emailParam,
        transactionType: type === "all" || !type ? "" : type,
      });

      const res = await loyaltyFetch(endpoint, { headers });
      if (isLoyaltyNotFoundHttpStatus(res.status)) {
        setTransactions([]);
        setTxNotEnrolled(true);
        return;
      }
      if (!res.ok) throw new Error("Erreur lors du chargement des transactions");

      const data = await res.json();
      setTransactions(Array.isArray(data) ? data : []);
    } catch (err) {
      setTxError(err.message || "Erreur inconnue");
    } finally {
      setTxLoading(false);
      setTxFetched(true);
    }
  };

  useEffect(() => {
    if (activeTab === "activiteUnomi" && !txFetched && unomiProfileId && brandName) {
      fetchTransactions(unomiProfileId, selectedTxType, contactEmail);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, txFetched, unomiProfileId, brandName, contactEmail]);

  const handleTxTypeChange = (e) => {
    const newType = e.target.value;
    setSelectedTxType(newType);
    if (unomiProfileId) {
      fetchTransactions(unomiProfileId, newType, contactEmail);
    }
  };

  // Trigger loyalty fetch when tab becomes active (only once per profile load)
  useEffect(() => {
    if (activeTab === "fideliteDetails" && !loyaltyFetched && unomiProfileId && brandName) {
      fetchLoyaltyData(unomiProfileId, contactEmail);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, loyaltyFetched, unomiProfileId, brandName, contactEmail]);

  usePostPurchasePolling({
    brandName,
    profileId: loyaltyProfileId,
    enabled: activeTab === "fideliteDetails" && Boolean(loyaltyProfileId && brandName),
    onPoll: () => {
      fetchLoyaltyData(unomiProfileId, contactEmail);
      setLoyaltyRefreshToken((t) => t + 1);
    },
  });

  const handleBack = () => {
    navigate('/contacts');
  };

  // ── Helpers ──────────────────────────────────────────────────
  const fmtDate = (v) => v ? new Date(v).toLocaleDateString('fr-FR') : '–';
  const fmtDateTime = (v) => v ? new Date(v).toLocaleString('fr-FR') : '–';

  // Progress percentage toward next tier
  const tierProgress = loyaltyInfo?.nextTier
    ? Math.min(100, ((loyaltyInfo.statusPoints || 0) / loyaltyInfo.nextTier.minStatusPoints) * 100)
    : 100;

  return (
    <div className="contact-detail-page">
      <div className="page-header">
        <IconButton
          onClick={handleBack}
          className="back-button"
          size="small"
        >
          <ArrowBackIos />
        </IconButton>
        <h1>Détail Contact : {contact?.properties?.firstName} {contact?.properties?.lastName}</h1>
      </div>

      <div className="contact-card-grid">
        <aside className="contact-summary-panel">
          {loading ? (
            <div style={{ textAlign: 'center', padding: '20px' }}>
              <CircularProgress />
            </div>
          ) : contact ? (
            <>
              <div className="profile-pic">
                {contact.properties?.firstName?.[0] || ''}{contact.properties?.lastName?.[0] || ''}
              </div>
              <h2>{contact.properties?.firstName} {contact.properties?.lastName}</h2>
              <p className="contact-email">{contact.properties?.email}</p>

              <div className="info-block">
                <label>ID Unomi:</label>
                <span><code>{contact.itemId}</code></span>
              </div>
              <div className="info-block">
                <label>Statut:</label>
                <span>{contact.properties?.city || 'N/A'}</span>
              </div>
              <div className="info-block">
                <label>Dernière Activité:</label>
                <span>
                  {contact.properties?.lastVisit
                    ? new Date(contact.properties.lastVisit).toLocaleString('fr-FR', {
                        day: '2-digit',
                        month: 'long',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit',
                        timeZone: 'UTC'
                      })
                    : 'N/A'}
                </span>
              </div>

              {loyaltyModuleActive && (
                <div className="info-block">
                  <label>Niveau Fidélité:</label>
                  <span style={{ fontWeight: "bold", color: "#f39c12" }}>
                    {loyaltyInfo?.currentTier?.name || loyaltyCustomer?.loyalty_status_name || loyaltyWallet?.loyaltyStatus || 'N/A'}
                  </span><br />
                  <label style={{ marginTop: "5px" }}>Solde points:</label>
                  <span>
                    {loyaltyCustomer?.points_balance ?? loyaltyCustomer?.status_points ?? loyaltyWallet?.pointsBalance ?? 0} pts
                  </span>
                </div>
              )}

              <div className="info-block">
                <label>tags UNOMI:</label>
                <div className="tags-list">
                  {contact.tags?.map((segment, index) => (
                    <span key={index}>{segment || <li>Aucun tags</li>}</span>
                  ))}
                </div>
              </div>

              <div className="action-buttons-contact">
                <button className="email-direct-btn">Envoyer un Email Direct</button>
                <button className="add-to-campaign-btn">Ajouter à une Campagne</button>
                {loyaltyModuleActive && (
                  <button style={{ backgroundColor: "#9b59b6", color: "white" }}>
                    Ajuster Points Fidélité
                  </button>
                )}
              </div>
            </>
          ) : (
            <div style={{ textAlign: 'center', padding: '20px' }}>
              <Typography variant="h6" color="error">
                Contact non trouvé
              </Typography>
            </div>
          )}
        </aside>

        <div className="contact-details-tabs">
          <nav className="tabs-nav">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                className={`tab-link ${activeTab === tab.id ? "active" : ""}`}
                onClick={() => setActiveTab(tab.id)}
              >
                {tab.label}
              </button>
            ))}
          </nav>

          {/* ── Profil Unomi ──────────────────────────────────── */}
          <div className={`tab-content ${activeTab === "profilUnomi" ? "active" : ""}`}>
            {loading ? (
              <div style={{ textAlign: 'center', padding: '20px' }}>
                <CircularProgress />
              </div>
            ) : contact ? (
              <>
                <h3>Propriétés du Profil (Unomi)</h3>
                <Box sx={{ mt: 2, display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))', gap: '16px' }}>
                  <Box>
                    <Typography variant="caption" color="textSecondary">Prénom</Typography>
                    <Typography variant="body1">{contact.properties?.firstName || 'N/A'}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="textSecondary">Nom</Typography>
                    <Typography variant="body1">{contact.properties?.lastName || 'N/A'}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="textSecondary">Email</Typography>
                    <Typography variant="body1">{contact.properties?.email || 'N/A'}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="textSecondary">Ville</Typography>
                    <Typography variant="body1">{contact.properties?.city || 'N/A'}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="textSecondary">Pays</Typography>
                    <Typography variant="body1">{contact.properties?.country || 'N/A'}</Typography>
                  </Box>
                </Box>

                <h3 style={{ marginTop: '24px' }}>Appartenance aux Segments (Unomi)</h3>
                {contact.segments && contact.segments.length > 0 ? (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                    {contact.segments.map((segment, index) => (
                      <Chip
                        key={index}
                        label={segment}
                        size="small"
                        sx={{
                          backgroundColor: colors.blueAccent[700],
                          color: 'white',
                        }}
                      />
                    ))}
                  </Box>
                ) : (
                  <Typography variant="body2" color="textSecondary" sx={{ mt: 1, fontStyle: 'italic' }}>
                    Aucun segment
                  </Typography>
                )}
              </>
            ) : (
              <Typography variant="h6" color="error" style={{ textAlign: 'center', padding: '20px' }}>
                Contact non trouvé
              </Typography>
            )}
          </div>

          {/* ── Engagement Email ──────────────────────────────── */}
          <div className={`tab-content ${activeTab === "engagementMautic" ? "active" : ""}`}>
            <h3>Historique d'Engagement Email (Mautic)</h3>
            <ul className="timeline">
              <li className="timeline-item">
                <span className="time">2024-05-10 10:05</span>
                <div className="event-title">Clic sur Email : "Promo Spéciale VIP"</div>
                <div className="event-details">Lien cliqué : <code>https://site.com/promo-vip</code></div>
              </li>
              <li className="timeline-item">
                <span className="time">2024-05-10 09:30</span>
                <div className="event-title">Ouverture Email : "Promo Spéciale VIP"</div>
              </li>
              <li className="timeline-item">
                <span className="time">2024-04-20 14:00</span>
                <div className="event-title">Email Envoyé : "Newsletter Avril"</div>
                <div className="event-details">Statut : Ouvert, Non Cliqué</div>
              </li>
            </ul>
          </div>

          {/* ── Activité & Événements ─────────────────────────── */}
            <div className={`tab-content ${activeTab === "activiteUnomi" ? "active" : ""}`}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2} flexWrap="wrap" gap={1}>
              <h3 style={{ margin: 0 }}>Historique d'Activité et Événements (Loyalty)</h3>
              <FormControl size="small" sx={{ minWidth: 200, bgcolor: 'white' }}>
                <InputLabel>Filtrer par type</InputLabel>
                <Select
                  value={selectedTxType}
                  label="Filtrer par type"
                  onChange={handleTxTypeChange}
                >
                  <MenuItem value="all">Tous les événements</MenuItem>
                  {transactionTypes.map(tt => (
                    <MenuItem key={tt.key || tt.id} value={tt.key || tt.id}>{tt.name || tt.key || tt.id}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            {txLoading ? (
              <Box display="flex" justifyContent="center" py={4}>
                <CircularProgress sx={{ color: "#3cb0d6" }} />
              </Box>
            ) : txNotEnrolled ? (
              <Alert severity="info" sx={{ mt: 2, borderRadius: 2 }}>
                {getLoyaltyNotEnrolledMessage({ email: contactEmail })}
              </Alert>
            ) : txError ? (
              <Alert severity="error" sx={{ mt: 2 }}>{txError}</Alert>
            ) : transactions.length === 0 ? (
              <Box py={4} textAlign="center" color="text.secondary">
                Aucun événement ou activité trouvé pour ce filtre.
              </Box>
            ) : (
              <ul className="timeline">
                {transactions.map(tx => (
                  <li className="timeline-item" key={tx.id}>
                    <span className="time">
                      {tx.created_at ? new Date(tx.created_at).toLocaleString('fr-FR', {
                        day: '2-digit', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit'
                      }) : 'N/A'}
                    </span>
                    <div className="event-title">
                      {tx.transaction_type}
                      <TransactionStatusChip transaction={tx} />
                      {tx.source && (
                        <Chip label={tx.source} size="small" sx={{ ml: 1, fontSize: 10, height: 20, bgcolor: "#f0f9ff", color: "#0369a1" }} />
                      )}
                    </div>
                    {String(tx?.status || "").toUpperCase() === "PROCESSED_ERRORS" && (
                      <Typography fontSize={12} color="warning.main" sx={{ mt: 0.5 }}>
                        Vente enregistrée, points non crédités
                        {tx.error_message ? ` — ${tx.error_message}` : ""}
                      </Typography>
                    )}
                    {tx.payload && Object.keys(tx.payload).length > 0 && (
                      <div className="event-details">
                        <details>
                          <summary style={{ cursor: 'pointer', color: '#3cb0d6', fontWeight: 600 }}>Détails de l'événement</summary>
                          <pre style={{ fontSize: 11, background: '#f8f9ff', padding: 12, borderRadius: 6, overflowX: 'auto', mt: 1, border: '1px solid #e8ecf1' }}>
                            {JSON.stringify(tx.payload, null, 2)}
                          </pre>
                        </details>
                      </div>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* ── Fidélité Détaillée ────────────────────────────── */}
          {loyaltyModuleActive && (
            <div className={`tab-content ${activeTab === "fideliteDetails" ? "active" : ""}`}>
              <h3>Détails du Programme de Fidélité (SGF)</h3>

              {loyaltyLoading && (
                <Box display="flex" justifyContent="center" py={4}>
                  <CircularProgress sx={{ color: "#3cb0d6" }} />
                </Box>
              )}

              {!loyaltyLoading && loyaltyError === LOYALTY_NOT_ENROLLED && (
                <Alert severity="info" sx={{ borderRadius: 2, mt: 1 }}>
                  {getLoyaltyNotEnrolledMessage({ email: contactEmail })}
                  {!brandName && " (Aucune marque sélectionnée — vérifiez le sélecteur de marque.)"}
                </Alert>
              )}

              {!loyaltyLoading && loyaltyError && loyaltyError !== LOYALTY_NOT_ENROLLED && (
                <Alert severity="error" sx={{ borderRadius: 2, mt: 1 }}>
                  {loyaltyError}
                </Alert>
              )}

              {!loyaltyLoading && !loyaltyError && loyaltyFetched && loyaltyWallet && (
                <>
                  {profileIdMismatch && (
                    <Alert severity="info" sx={{ borderRadius: 2, mb: 2 }}>
                      Profil Unomi fusionné : l’identifiant CDP ({unomiProfileId}) diffère du profileId fidélité actuel ({loyaltyCustomer.profile_id}).
                      Les données sont réconciliées via l’email {contactEmail ? `(${contactEmail})` : ""}.
                    </Alert>
                  )}
                  {/* ── KPI Header ─────────────────────────────── */}
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fill, minmax(170px, 1fr))',
                      gap: 2,
                      mb: 3,
                      mt: 1,
                    }}
                  >
                    <Box textAlign="center" p={2} bgcolor="#fafbfd" borderRadius={2} border="1px solid #e8ecf1">
                      <Typography fontSize={11} color="text.secondary" fontWeight={600} mb={0.5} sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                        Solde points
                      </Typography>
                      <Typography variant="h5" fontWeight={800} color="#3cb0d6">
                        {getPointsBalance(loyaltyInfo, loyaltyCustomer)}
                      </Typography>
                    </Box>

                    <Box textAlign="center" p={2} bgcolor="#fafbfd" borderRadius={2} border="1px solid #e8ecf1">
                      <Typography fontSize={11} color="text.secondary" fontWeight={600} mb={0.5} sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                        Niveau
                      </Typography>
                      <Typography variant="h5" fontWeight={800} color="#FFD93D">
                        {getTierLabel(loyaltyInfo, loyaltyCustomer)}
                      </Typography>
                    </Box>
                  </Box>

                  {/* ── Dates de validité ─────────────────────── */}
                  {loyaltyCustomer && (
                    <CustomerValidityDatesPanel
                      customer={loyaltyCustomer}
                      movements={loyaltyMovements}
                      loyalty={loyaltyInfo}
                      movementsLoadError={loyaltyLoadErrors.pointMovements}
                    />
                  )}

                  {loyaltyLoadErrors.emailMissing && (
                    <Alert severity="warning" sx={{ mb: 2, borderRadius: 2 }}>
                      {loyaltyLoadErrors.emailMissing}
                    </Alert>
                  )}
                  {loyaltyLoadErrors.coupons && entitlementsTab === 0 && (
                    <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
                      Impossible de charger les coupons : {loyaltyLoadErrors.coupons}
                    </Alert>
                  )}

                  {/* ── Tier Progression Bar ───────────────────── */}
                  {loyaltyInfo?.currentTier && (
                    <Box mb={3} p={2} bgcolor="#f8f9ff" borderRadius={2} border="1px solid #e8ecf1">
                      <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                        <Typography fontSize={13} fontWeight={600} color="#334155">
                          Progression — Niveau actuel : <strong>{loyaltyInfo.currentTier.name}</strong>
                        </Typography>
                        {loyaltyInfo.nextTier && (
                          <Typography fontSize={12} color="text.secondary">
                            {loyaltyInfo.pointsToNextTier ?? '?'} SP avant <strong>{loyaltyInfo.nextTier.name}</strong>
                          </Typography>
                        )}
                      </Box>
                      <Box display="flex" alignItems="center" gap={2}>
                        <LinearProgress
                          variant="determinate"
                          value={tierProgress}
                          sx={{
                            flexGrow: 1,
                            height: 8,
                            borderRadius: 4,
                            bgcolor: '#e2e8f0',
                            '& .MuiLinearProgress-bar': { bgcolor: '#3cb0d6' },
                          }}
                        />
                        <Typography variant="caption" fontWeight={600} color="#3cb0d6">
                          {loyaltyInfo.statusPoints} SP
                        </Typography>
                      </Box>
                    </Box>
                  )}

                  {/* ── Coupons & historique entitlements ───────── */}
                  <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1} mb={1}>
                    <Typography variant="h6" fontWeight={700} color="#1a1a2e" fontSize={15}>
                      Coupons & récompenses
                    </Typography>
                    <Tabs
                      value={entitlementsTab}
                      onChange={(_, v) => setEntitlementsTab(v)}
                      sx={{ minHeight: 32, "& .MuiTab-root": { textTransform: "none", minHeight: 32, fontSize: 12, fontWeight: 600 } }}
                    >
                      <Tab label={`Portefeuille (${loyaltyCouponsWithRewards.length})`} />
                      <Tab label="Historique" />
                    </Tabs>
                  </Box>

                  {entitlementsTab === 0 && (
                    <Box sx={{ mb: 3 }}>
                      <CustomerCouponsEntitlementsTable
                        items={loyaltyCouponsWithRewards}
                        rewardDefs={rewardDefs}
                        catalogStatusLabels={catalogStatusLabels}
                        compact
                      />
                    </Box>
                  )}

                  {entitlementsTab === 1 && (
                    <Paper sx={{ p: 2, mb: 3, borderRadius: 2, border: "1px solid #e8ecf1" }}>
                      <EntitlementsHistoryPanel
                        mode="client"
                        brandName={brandName}
                        profileId={loyaltyProfileId}
                        email={contactEmail}
                      />
                    </Paper>
                  )}

                  <Box sx={{ mb: 3 }}>
                    <SaleTransactionsPanel
                      brandName={brandName}
                      profileId={loyaltyProfileId}
                      email={contactEmail}
                      refreshToken={loyaltyRefreshToken}
                    />
                  </Box>

                  {/* ── Historique des Points ──────────────────── */}
                  <Typography variant="h6" fontWeight={700} mb={1.5} color="#1a1a2e" fontSize={15}>
                    Historique des Points ({loyaltyMovements.length})
                  </Typography>
                  {loyaltyLoadErrors.pointMovements && (
                    <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
                      Impossible de charger l'historique pour le moment — {loyaltyLoadErrors.pointMovements}
                    </Alert>
                  )}
                  <TableContainer
                    component={Paper}
                    elevation={0}
                    sx={{ borderRadius: 2, border: "1px solid #e8ecf1" }}
                  >
                    <Table size="small">
                      <TableHead>
                        <TableRow sx={{ backgroundColor: '#f8f9ff' }}>
                          <TableCell sx={{ fontWeight: 600, fontSize: 13 }}>Date</TableCell>
                          <TableCell sx={{ fontWeight: 600, fontSize: 13 }}>Type</TableCell>
                          <TableCell sx={{ fontWeight: 600, fontSize: 13 }}>Variation</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {loyaltyMovements.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={3} align="center" sx={{ py: 3, color: 'text.secondary', fontStyle: 'italic' }}>
                              {loyaltyLoadErrors.pointMovements
                                ? "Historique indisponible."
                                : "Aucun mouvement enregistré."}
                            </TableCell>
                          </TableRow>
                        ) : loyaltyMovements.map((row) => (
                          <TableRow key={row.id} hover>
                            <TableCell sx={{ fontSize: 13 }}>{fmtDateTime(row.created_at)}</TableCell>
                            <TableCell>
                              <Chip
                                label={row.type}
                                size="small"
                                variant="outlined"
                                sx={{ fontWeight: 600, fontSize: 11, borderRadius: 1 }}
                              />
                            </TableCell>
                            <TableCell>
                              <Typography
                                fontWeight={700}
                                fontSize={13}
                                color={
                                  row.points > 0
                                    ? 'success.main'
                                    : row.points < 0
                                    ? 'error.main'
                                    : 'text.secondary'
                                }
                              >
                                {row.points > 0 ? `+${row.points}` : row.points}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
